import { VStack, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import LoadingIndicator from "../components/Loading";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { PageTitle } from "../components/text/PageTitle";
import { useCustomerUsersCustomers, useJobs } from "@api/hooks";
import { routes } from "../routes";
import SimpleSelect from "../components/ChakraUI/SimpleSelect";
import JobListData, { GroupedJobs, JobListSection } from "../components/jobList/JobListData";
import NewJobButton from "../components/jobList/NewJobButton";
import ToggleSwitch from "../components/ToggleSwitch";
import { getErrorInFetchingData } from "@utils/utils";
import useStorage from "@utils/UseStorage";
import { useQueryClient } from "@tanstack/react-query";

const sections: { key: JobListSection; name: string }[] = [
	{ key: "onGoing", name: "Pågående" },
	{ key: "upComing", name: "Kommande" },
	{ key: "past", name: "Historiska" },
];

const JobsPage = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { clear } = useStorage();
	const queryCache = useQueryClient();
	const [section, setSection] = useState<JobListSection>("onGoing");
	const [selectedCustomer, setSelectedCustomer] = useState("");
	const { customers, isLoading: isLoadingCustomers } = useCustomerUsersCustomers();
	const { data, isLoading, isFetching } = useJobs(1, 5, undefined, selectedCustomer, true, undefined, (code) => {
		if (code === "MUST_BE_LOGGED_IN") {
			clear();
			queryCache.clear();
			navigate(routes.signIn);
		}
		toast({
			title: getErrorInFetchingData(code),
			status: "error",
			duration: 9000,
			isClosable: true,
			variant: "subtle",
		});
	});

	const customerOptions =
		customers?.customers?.map((customer) => ({
			key: customer.id,
			value: customer?.name || "",
		})) || [];
	const grouped = data as GroupedJobs | undefined;
	const sectionOptions = sections.map(({ key, name }) => ({
		key,
		name: grouped?.[key] ? `${name} (${grouped[key].length})` : name,
	}));

	// Also while loading, so the spinner keeps the filter's place
	const showUnitFilter = isLoadingCustomers || (customers?.customers?.length ?? 0) > 1;

	return (
		<LoggedInLayout width="full">
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full" pb={10}>
				<PageTitle showNotifications>Uppdrag</PageTitle>

				{/* Unit filter left, sections centered, new job right; without a unit filter the
				    sections move to the left. Sticks to the top of the viewport on md+ so the
				    filters stay reachable while the list scrolls. */}
				<div
					className={`grid w-full grid-cols-1 items-center gap-4 bg-white md:sticky md:top-0 md:z-10 md:!-mt-4 md:py-4 ${
						showUnitFilter ? "md:grid-cols-[1fr_auto_1fr]" : "md:grid-cols-[1fr_auto]"
					}`}
				>
					{showUnitFilter && (
						<div className="flex justify-center md:justify-start">
							{isLoadingCustomers ? (
								<LoadingIndicator size={"s"} />
							) : (
								// Full width on mobile; SimpleSelect's built-in right margin is dropped
								<div className="w-full md:w-[250px] [&>div]:!mr-0">
									<SimpleSelect
										fontSize={"16px"}
										width="100%"
										value={selectedCustomer}
										onChange={(e) => setSelectedCustomer(e.target.value)}
										options={customerOptions}
										placeHolder="Alla enheter"
									/>
								</div>
							)}
						</div>
					)}
					<div className={`flex justify-center ${showUnitFilter ? "" : "md:justify-start"}`}>
						<ToggleSwitch
							optionData={sectionOptions}
							value={section}
							onChange={(key) => setSection(key as JobListSection)}
							stretchOnMobile
						/>
					</div>
					{/* First on mobile, right column on desktop */}
					<div className="order-first flex justify-end md:order-none">
						<NewJobButton />
					</div>
				</div>

				{!isLoadingCustomers && (
					<JobListData
						isLoading={isLoading || isFetching}
						section={section}
						jobData={data}
						isFiltered={!!selectedCustomer}
						onChangeSection={setSection}
					/>
				)}
			</VStack>
		</LoggedInLayout>
	);
};

export default JobsPage;
